// src/pages/AdminDashboard.js
import { useState } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, Link } from 'react-router-dom';
import { FiLogOut, FiMessageSquare, FiLink, FiDollarSign, FiBriefcase, FiImage, FiDatabase } from 'react-icons/fi';
import { useApiAuth } from '../hooks/useApiAuth';

import TestimonialsManager from '../components/admin/TestimonialsManager';
import SocialLinksManager from '../components/admin/SocialLinksManager';
import PricingManager from '../components/admin/PricingManager';
import PortfolioManager from '../components/admin/PortfolioManager';
import ImageManager from '../components/admin/ImageManager';

const TABS = [
  { id: 'testimonials', label: 'Reviews', icon: FiMessageSquare },
  { id: 'social',       label: 'Social Links', icon: FiLink },
  { id: 'pricing',      label: 'Pricing', icon: FiDollarSign },
  { id: 'portfolio',    label: 'Portfolio', icon: FiBriefcase },
  { id: 'images',       label: 'Images', icon: FiImage },
];

function AdminDashboard() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { authFetch } = useApiAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('testimonials');
  const [seedStatus, setSeedStatus] = useState('idle'); // idle | seeding | done | error

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const displayName = user?.firstName
    || user?.emailAddresses?.[0]?.emailAddress
    || 'Admin';

  return (
    <div className="admin-page">
      <aside className="admin-sidebar">
        <Link to="/">
          <img src="/logo.png" alt="CSdevs" className="admin-logo-img" />
        </Link>

        <nav className="admin-nav">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={`admin-nav-item${activeTab === id ? ' active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </nav>

        <button
          className="admin-seed-btn"
          onClick={async () => {
            setSeedStatus('seeding');
            try {
              const res = await authFetch('/api/admin/seed', { method: 'POST', body: {} });
              setSeedStatus('done');
            } catch {
              setSeedStatus('error');
            }
          }}
          disabled={seedStatus === 'seeding' || seedStatus === 'done'}
        >
          <FiDatabase size={14} />
          {seedStatus === 'idle' && 'Initialize DB'}
          {seedStatus === 'seeding' && 'Setting up…'}
          {seedStatus === 'done' && 'DB Ready ✓'}
          {seedStatus === 'error' && 'Retry Init'}
        </button>

        <button className="admin-signout" onClick={handleSignOut}>
          <FiLogOut size={14} />
          Sign out
        </button>
      </aside>

      <main className="admin-main">
        <header className="admin-header">
          <h1 className="admin-title">
            {TABS.find(t => t.id === activeTab)?.label || 'Dashboard'}
          </h1>
          <p className="admin-subtitle">Welcome back, {displayName}</p>
        </header>

        {activeTab === 'testimonials' && <TestimonialsManager />}
        {activeTab === 'social' && <SocialLinksManager />}
        {activeTab === 'pricing' && <PricingManager />}
        {activeTab === 'portfolio' && <PortfolioManager />}
        {activeTab === 'images' && <ImageManager />}
      </main>
    </div>
  );
}

export default AdminDashboard;
